import { useLocation, useNavigate } from "react-router";
import type { PlayerProfile } from "@wr/contracts";
import { t, type StringKey } from "@wr/i18n";
import { Avatar, ChromeNickname, NavDrawer, NavItem, RoleChip } from "@wr/ui";
import { useOverlay } from "../app/OverlayProvider";
import { useShopEnabled } from "../hooks/useShopEnabled";
import { LegalLink } from "./LegalAttestation";

/**
 * **LOCKED (design spec §11): the v1 item list is exactly this.**
 *
 * Lobby, Shop, Profile, Settings, Log out. No Friends, Tournaments,
 * Leaderboard, News, Awards or How-to-play — the desktop mock's nav pills are
 * exploration of a post-v1 feature set, not scope.
 *
 * The list is config-driven so S3 adds a row by adding an entry; the S2 config
 * contains only these. A `soon` row renders the `SOON` chip, is `aria-disabled`
 * and its tap does nothing at all — no toast, because the chip already said it.
 *
 * **T-440: `soon` is no longer a constant on the Shop row.** Ivy's
 * SHOP-ENTRY-POINTS spec (storefront ruling addendum-d §1) makes it *"derived
 * from the flag, not constants"*, so the row carries `shopGated` — a statement
 * about *which flag decides this row*, not about what it currently renders. The
 * flag itself lives in exactly one place (`useShopEnabled`), and the glyph and
 * the active treatment need no branch at all: `NavItem` already draws the
 * `wr-icon-nav-shop` ART-SLOT on the `coin` accent tile, and the standard
 * active-row treatment already keys off `location.pathname`.
 */
const NAV_ITEMS: Array<{
  key: StringKey;
  to: string;
  monogram: string;
  accent: string;
  /** The row is `SOON` exactly while the shop is closed (addendum-d §1). */
  shopGated?: boolean;
  testId: string;
}> = [
  { key: "nav.lobby", to: "/lobby", monogram: "L", accent: "play", testId: "nav-lobby" },
  {
    key: "nav.shop",
    to: "/shop",
    monogram: "S",
    accent: "coin",
    shopGated: true,
    testId: "nav-shop",
  },
  { key: "nav.profile", to: "/profile", monogram: "P", accent: "good", testId: "nav-profile" },
  {
    key: "nav.settings",
    to: "/settings",
    monogram: "⚙",
    accent: "muted",
    testId: "nav-settings",
  },
];

export interface NavDrawerContentProps {
  profile: PlayerProfile;
  onLogout: () => void;
}

export function NavDrawerContent({ profile, onLogout }: NavDrawerContentProps) {
  const { overlay, close, open } = useOverlay();
  const navigate = useNavigate();
  const location = useLocation();
  const shopEnabled = useShopEnabled();

  function go(to: string) {
    close();
    void navigate(to);
  }

  return (
    <NavDrawer open={overlay === "drawer"} onClose={close} label={t("nav.menu")}>
      <button
        type="button"
        className="wr-drawer__user"
        data-testid="drawer-user"
        onClick={() => {
          close();
          open("flyout");
        }}
      >
        <Avatar
          playerId={profile.id}
          nickname={profile.nickname}
          url={profile.avatarUrl}
          size={40}
        />
        {/*
          §14.1 ruling B item 4: the drawer's user row is fixed-width chrome and
          falls under the same one-line rule as the flyout. `min-width: 0` on
          the parent is what lets the name shrink far enough to ellipsise.
        */}
        <span className="wr-stack" style={{ gap: 4, minWidth: 0, alignItems: "flex-start" }}>
          {/*
            **`nameExposure="ancestor"` — earned, not assumed (S14 `A5`, R-K3).**
            The enclosing `<button className="wr-drawer__user">` carries **no
            `aria-label` of its own**, so it names itself from its contents, and
            those contents include this nickname as DOM text (`text-overflow`
            truncates paint, never the text). The button's accessible name
            therefore already carries the **full, untruncated** nickname — the
            surface exposes it exactly once, which is R-K4.

            The inner `aria-label` this replaces was inert twice over: the
            button's ARIA children are presentational, and the label duplicated
            the text it sat on. Removing it cannot move the button's name, which
            the arms in `seat-and-chrome.test.tsx` assert before and after.
          */}
          <ChromeNickname nickname={profile.nickname} base="display-sm" nameExposure="ancestor" />
          {profile.isGuest ? <RoleChip label={t("role.guest")} /> : null}
        </span>
      </button>

      <hr className="wr-drawer__rule" />

      <ul className="wr-nav">
        {NAV_ITEMS.map((item) => {
          /**
           * Flag off ⇒ exactly the S2 §11 stub this row has always been: the
           * `SOON` chip, `aria-disabled`, a tap that does nothing. Flag on ⇒
           * the chip is gone and the row is an ordinary destination.
           */
          const soon = item.shopGated === true && !shopEnabled;
          return (
            <NavItem
              key={item.to}
              label={t(item.key)}
              monogram={item.monogram}
              accent={item.accent}
              active={location.pathname === item.to}
              testId={item.testId}
              {...(soon ? { soonLabel: t("nav.soon") } : {})}
              onSelect={() => go(item.to)}
            />
          );
        })}
      </ul>

      <div className="wr-drawer__footer">
        <hr className="wr-drawer__rule" />
        <ul className="wr-nav">
          <NavItem
            label={t("nav.logout")}
            monogram="→"
            accent="muted"
            testId="nav-logout"
            onSelect={onLogout}
          />
        </ul>

        {/*
          T-234 — the in-app home for the two legal documents.

          **Not a sixth `NavItem`.** §11's item list is LOCKED at five rows and
          adding a destination to it is exactly what that lock forbids; a footer
          footnote is a different thing from a place you go. This is the region
          §20.5 nominated for the optional build line, so the two stack.

          **They do not `close()` the drawer**, unlike every row above: both open
          in a new tab (`LegalLink`), so the drawer is still where the player
          left it when they come back — and closing it would be the app
          rearranging a screen the player never navigated away from. This is the
          one deliberate exception to `go()`.
        */}
        <div className="wr-drawer__legal wr-body-xs">
          <LegalLink to="/terms" label={t("legal.terms.title")} testId="drawer-terms" />
          <span aria-hidden="true">·</span>
          <LegalLink to="/privacy" label={t("legal.privacy.title")} testId="drawer-privacy" />
        </div>
      </div>
    </NavDrawer>
  );
}
